import { useNavigate } from "react-router-dom";
import { Tag, Text } from "@app/ui";
import { listAllPassports, listAllProducts } from "../api/platform.js";
import { useFormatDateTime, useT } from "../i18n.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { CrossTenantList } from "../platform/CrossTenantList.js";
import type { AdminPassportRow, AdminProductRow } from "../types.js";

/**
 * Platform console: every product / passport across all tenants. A row click
 * steps into the owning org (OrgContext) and opens the regular detail page, so
 * editing reuses the tenant screens — the API authorizes it via the org-scope
 * bypass for platform admins.
 */

export function PlatformProductsPage() {
  const t = useT();
  const fmt = useFormatDateTime();
  const navigate = useNavigate();
  const { selectOrg } = useOrgs();
  const statusColor = { active: "success", draft: "warning", archived: "default" } as const;

  return (
    <CrossTenantList<AdminProductRow>
      title={t("platform.allProducts")}
      fetch={listAllProducts}
      searchPlaceholder={t("platform.searchProducts")}
      empty={t("platform.noProducts")}
      rowKey={(p) => p.id}
      statuses={(["active", "draft", "archived"] as const).map((s) => ({
        value: s,
        label: t(`productStatus.${s}`),
      }))}
      onRowClick={(p) => {
        selectOrg(p.orgId);
        navigate(`/products/${p.id}`);
      }}
      columns={[
        {
          key: "name",
          title: t("platform.product"),
          render: (p) => (
            <div className="min-w-0">
              <div className="font-medium">{p.name}</div>
              <Text small secondary>
                {p.sku}
                {p.gtin ? ` · GTIN ${p.gtin}` : ""}
              </Text>
            </div>
          ),
        },
        {
          key: "org",
          title: t("adminOrgs.name"),
          render: (p) => (
            <span className="inline-flex flex-wrap items-center gap-1">
              {p.orgName}
              {p.isDemo && <Tag color="primary">{t("platform.demoTag")}</Tag>}
            </span>
          ),
        },
        {
          key: "status",
          title: t("common.status"),
          render: (p) => <Tag color={statusColor[p.status]}>{t(`productStatus.${p.status}`)}</Tag>,
        },
        { key: "passports", title: t("nav.passports"), numeric: true, render: (p) => p.passportCount },
        {
          key: "updated",
          title: t("common.updatedAt"),
          render: (p) => fmt(p.updatedAt),
          hideOnMobile: true,
        },
      ]}
    />
  );
}

export function PlatformPassportsPage() {
  const t = useT();
  const fmt = useFormatDateTime();
  const navigate = useNavigate();
  const { selectOrg } = useOrgs();
  const statusColor = { published: "success", draft: "warning", archived: "default" } as const;

  return (
    <CrossTenantList<AdminPassportRow>
      title={t("platform.allPassports")}
      fetch={listAllPassports}
      searchPlaceholder={t("platform.searchPassports")}
      empty={t("platform.noPassports")}
      rowKey={(p) => p.id}
      statuses={(["published", "draft", "archived"] as const).map((s) => ({
        value: s,
        label: t(`passportStatus.${s}`),
      }))}
      onRowClick={(p) => {
        selectOrg(p.orgId);
        navigate(`/passports/${p.id}`);
      }}
      columns={[
        {
          key: "product",
          title: t("platform.product"),
          render: (p) => (
            <div className="min-w-0">
              <div className="font-medium">{p.productName}</div>
              <Text small secondary>
                {p.sku}
              </Text>
            </div>
          ),
        },
        {
          key: "org",
          title: t("adminOrgs.name"),
          render: (p) => (
            <span className="inline-flex flex-wrap items-center gap-1">
              {p.orgName}
              {p.isDemo && <Tag color="primary">{t("platform.demoTag")}</Tag>}
            </span>
          ),
        },
        {
          key: "status",
          title: t("common.status"),
          render: (p) => <Tag color={statusColor[p.status]}>{t(`passportStatus.${p.status}`)}</Tag>,
        },
        {
          key: "version",
          title: t("platform.version"),
          numeric: true,
          render: (p) => (p.currentVersionNo ? `v${p.currentVersionNo}` : "—"),
        },
        {
          key: "public",
          title: t("platform.publicPage"),
          hideOnMobile: true,
          render: (p) =>
            p.status === "published" ? (
              // Plain link: /p/ is served by the API, not the SPA router.
              <a
                href={`/p/${encodeURIComponent(p.publicSlug)}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                /p/{p.publicSlug}
              </a>
            ) : (
              "—"
            ),
        },
        {
          key: "updated",
          title: t("common.updatedAt"),
          render: (p) => fmt(p.updatedAt),
          hideOnMobile: true,
        },
      ]}
    />
  );
}
